import { FormHandler } from '@framework/form/form/FormHandler.tsx';
import { type YupAnyObjectSchema } from '@framework/validation/YupObjectSchema.ts';
import type * as React from 'react';
import { useCallback } from 'react';
import { type AnyObject } from 'yup';
import { type IAdvancedSearchCollapseFormProps } from '../AdvancedSearchCollapseForm.tsx';
import { AdvancedSearchQueryValuesSetter } from '../component/AdvancedSearchQueryValuesSetter.tsx';
import { FilterBarLayout } from './FilterBarLayout.tsx';
import { useIsFiltered } from './hooks/useIsFiltered.ts';
import { useSearchableFormQuery } from './hooks/useSearchableFormQuery.ts';

export interface IFilterBarLayout {
  /**
   * Aggiunge padding orizzontale (e superiore sulla prima riga) ai
   * `LayoutRowWrapper` interni per integrare visivamente la FilterBar
   * quando è renderizzata dentro a un `Card`.
   */
  showInCard?: boolean;
}

export interface IFilterBarProps<
  TQuery extends TSearchable,
  TSearchable extends AnyObject,
> extends Omit<
    IAdvancedSearchCollapseFormProps<TQuery, TSearchable>,
    // `defaultFieldName` serviva al vecchio form (campo proposto a ricerca
    // vuota): la FilterBar parte vuota di proposito (paradigma a chip) e lo
    // ignora a runtime. Rimosso dal tipo per non lasciare una prop morta.
    'defaultFieldName'
  > {
  /**
   * Se `true`, mostra il bottone standard "Aggiorna" a destra della toolbar.
   * Al click la FilterBar aggiorna internamente `__cacheInvalidator` nella
   * query: React Query rileva il cambio di queryKey e rifa la fetch.
   * Nessuna integrazione con `queryClient` richiesta nel consumer.
   */
  showRefresh?: boolean;
  /**
   * Azioni custom aggiuntive a destra della toolbar (es. Colonne, Esporta CSV).
   * Vengono renderizzate DOPO le azioni standard interne (Aggiorna).
   */
  extraActions?: React.ReactNode;
  /** Opzioni di layout per la FilterBar. */
  layout?: IFilterBarLayout;
}

/**
 * FilterBar "chip-based": variante drop-in di `AdvancedSearchCollapseForm`.
 *
 * Riusa la stessa logica di FormHandler / transform / pageKey /
 * __cacheInvalidator / isFiltered. Cambia SOLO il rendering: chip + popover
 * invece di righe verticali con collapse.
 */
export function FilterBar<
  TQuery extends TSearchable,
  TSearchable extends AnyObject = Record<string, any>,
>(props: Readonly<IFilterBarProps<TQuery, TSearchable>>) {
  const {
    id,
    fields,
    schema,
    query,
    setQuery,
    defaultQuery,
    transform,
    pageKey,
    pageDefaultIndex = 0,
    onReset,
    disableLocalStorage,
    isFetching,
    total,
    ignoredKeys = [],
    messageListProps,
    showRefresh,
    extraActions,
    layout,
    sortingFields,
  } = props;

  const { handleSearch, defaultInitialValues, parsedQuery } =
    useSearchableFormQuery<TQuery, TSearchable>({
      query,
      setQuery,
      defaultQuery,
      schema,
      transform,
      pageKey,
      pageDefaultIndex,
      onReset,
    });

  const isFiltered = useIsFiltered({
    query,
    defaultQuery,
    isFetching,
    ignoredKeys,
  });

  const onResetAll = useCallback(() => {
    setQuery({ ...defaultQuery });
  }, [setQuery, defaultQuery]);

  // `schema` arriva tipizzato come `yup.ObjectSchema<TSearchable>` (generico sul
  // tipo del form), mentre FormHandler e i componenti figli accettano lo schema
  // "generico" `YupAnyObjectSchema` (= ObjectSchema<any, ...>). I generics di Yup
  // sono invarianti, quindi il primo non è assegnabile al secondo: serve il cast.
  // Lo facciamo una volta sola qui e lo riusiamo. Stesso pattern di AdvancedSearch.
  const anySchema = schema as YupAnyObjectSchema;

  // Refresh interno: stesso pattern di __cacheInvalidator usato da handleSearch.
  // Cambiare il valore fa cambiare la queryKey di React Query → refetch.
  const onRefresh = useCallback(() => {
    setQuery({
      ...query,
      ...({ __cacheInvalidator: Date.now() } as any),
    } as TQuery);
  }, [setQuery, query]);

  return (
    <FormHandler
      nested
      schema={anySchema}
      initialValues={defaultInitialValues}
      onReset={() => {
        handleSearch(null);
      }}
      hideVisualFeedback
      onSubmit={async values => {
        try {
          handleSearch(values as TSearchable);
        } catch (e) {
          console.error('[FilterBar] Errore nella ricerca', e);
        }
      }}
    >
      <AdvancedSearchQueryValuesSetter query={parsedQuery} schema={anySchema} />
      <FilterBarLayout
        id={id}
        fields={fields}
        query={parsedQuery}
        schema={anySchema}
        disableLocalStorage={disableLocalStorage}
        isFiltered={isFiltered}
        total={total}
        onResetAll={onResetAll}
        onRefresh={showRefresh ? onRefresh : undefined}
        extraActions={extraActions}
        sortingFields={sortingFields}
        messageListMessage={messageListProps?.message}
        resetButtonProps={messageListProps?.resetButtonProps}
        layout={layout}
      />
    </FormHandler>
  );
}
